import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  AlertTriangle, Building, Check, DollarSign,
  FileText, Loader2, MapPin, Shield, Star, Wrench, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PAYMENT_PLANS,
  WARRANTIES,
  PRODUCTS,
  PROJECT_TYPES,
  HOA_APPROVAL_STATUS,
  US_STATES,
  getOptionLabel
} from '../../config/jobFieldOptions';
import { CapacityIndicator, CapacityFullModal, useCapacityCheck } from '../capacity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Save Status Types
const SAVE_STATUS = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error'
};

// Role-based field permissions
const getFieldPermissions = (userRole, source) => {
  const isAdmin = ['super_admin', 'admin'].includes(userRole);
  const isSales = ['sales', 'sales_rep', 'sales_manager', 'director_of_sales'].includes(userRole);
  const isOps = ['ops', 'operations', 'ops_manager', 'foreman', 'project_manager'].includes(userRole);
  const isCallCenter = ['call_center', 'call_center_rep', 'call_center_manager'].includes(userRole) || source === 'callcenter';

  return {
    // Financial fields - Ops read-only
    financials: isAdmin || isSales,
    // Production notes - Ops + Admin can edit
    productionNotes: isAdmin || isOps,
    // Scheduling fields - All can edit except Call Center
    scheduling: isAdmin || isSales || isOps,
    // General fields - Call Center mostly read-only
    general: !isCallCenter || isAdmin,
    // Full access for admin
    isAdmin,
    isCallCenter
  };
};

// Editable Field Component with autosave
const EditableField = ({
  label,
  value,
  field,
  type = 'text',
  options = [],
  required = false,
  disabled = false,
  placeholder = '',
  onSave,
  multiline = false,
  className = ''
}) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(false);

  // Sync local value when prop changes (only when not focused)
  const prevValue = React.useRef(value);
  if (prevValue.current !== value && !isFocused) {
    setLocalValue(value ?? '');
    prevValue.current = value;
  }

  const handleBlur = () => {
    setIsFocused(false);
    if (localValue !== value) {
      onSave(field, localValue);
    }
  };

  if (type === 'select') {
    return (
      <div className={`space-y-1.5 ${className}`}>
        <Label className="text-sm font-medium text-slate-600 dark:text-slate-400">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <Select
          value={localValue || ''}
          onValueChange={(val) => {
            setLocalValue(val);
            onSave(field, val);
          }}
          disabled={disabled}
        >
          <SelectTrigger className="h-9 bg-white dark:bg-slate-800">
            <SelectValue placeholder={placeholder || `Select ${label}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (type === 'switch') {
    return (
      <div className={`flex items-center justify-between py-2 ${className}`}>
        <Label className="text-sm font-medium text-slate-600 dark:text-slate-400">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <Switch
          checked={!!localValue}
          onCheckedChange={(checked) => {
            setLocalValue(checked);
            onSave(field, checked);
          }}
          disabled={disabled}
        />
      </div>
    );
  }

  if (multiline) {
    return (
      <div className={`space-y-1.5 ${className}`}>
        <Label className="text-sm font-medium text-slate-600 dark:text-slate-400">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <Textarea
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`min-h-[100px] bg-white dark:bg-slate-800 resize-y ${
            isFocused ? 'ring-2 ring-blue-500' : ''
          }`}
        />
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-sm font-medium text-slate-600 dark:text-slate-400">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        type={type}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`h-9 bg-white dark:bg-slate-800 ${
          isFocused ? 'ring-2 ring-blue-500' : ''
        }`}
      />
    </div>
  );
};

// Read-only Field Component
const ReadOnlyField = ({ label, value, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</Label>
    <div className="h-9 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-300">
      {value || '—'}
    </div>
  </div>
);

// Section Header Component
const SectionHeader = ({ icon: Icon, title, children }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-blue-500" />
      <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
    </div>
    {children}
  </div>
);

// Main Component
export const ProjectDetailsTab = ({
  project,
  user,
  users = [],
  markets = [],
  source = 'sales',
  onRefresh
}) => {
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const [lastSaved, setLastSaved] = useState(null);
  const [retryField, setRetryField] = useState(null);

  const permissions = getFieldPermissions(user?.role, source);

  // Capacity check hook and state
  const capacityCheck = useCapacityCheck();
  const [capacityConflictOpen, setCapacityConflictOpen] = useState(false);
  const [pendingDateSave, setPendingDateSave] = useState(null);
  const [capacityConflict, setCapacityConflict] = useState(null);
  
  // Check if user can override capacity
  const canOverrideCapacity = ['project_manager', 'operations_manager', 'admin', 'super_admin', 'ops_manager'].includes(user?.role?.toLowerCase());
  
  // Check capacity when estimated_start_date changes
  useEffect(() => {
    if (project?.market_id && project?.estimated_start_date) {
      capacityCheck.checkAvailability(project.market_id, project.estimated_start_date, 500);
    }
  }, [project?.market_id, project?.estimated_start_date]);

  // Calculate duration if both dates exist
  const duration = useMemo(() => {
    if (project?.estimated_start_date && project?.completed_date) {
      const start = new Date(project.estimated_start_date);
      const end = new Date(project.completed_date);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
    return '—';
  }, [project?.estimated_start_date, project?.completed_date]);

  // Save field to backend
  const handleSave = async (field, value) => {
    if (!project?.id) return;

    setSaveStatus(SAVE_STATUS.SAVING);

    try {
      await axios.patch(`${API}/jobs/${project.id}`, {
        [field]: value
      });

      setSaveStatus(SAVE_STATUS.SAVED);
      setLastSaved(new Date());
      setRetryField(null);

      // Reset to idle after 2 seconds
      setTimeout(() => {
        setSaveStatus(prev => prev === SAVE_STATUS.SAVED ? SAVE_STATUS.IDLE : prev);
      }, 2000);

      // Refresh parent data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to save field:', field, error);
      setSaveStatus(SAVE_STATUS.ERROR);
      setRetryField({ field, value });
      toast.error(`Failed to save ${field.replace(/_/g, ' ')}`);
    }
  };

  // Retry failed save
  const handleRetry = () => {
    if (retryField) {
      handleSave(retryField.field, retryField.value);
    }
  };

  // Handle schedule date save with capacity check
  const handleScheduleDateSave = async (field, value) => {
    // Only check capacity for estimated_start_date on projects with a market
    if (field !== 'estimated_start_date' || !project?.market_id) {
      return handleSave(field, value);
    }
    
    setPendingDateSave({ field, value });
    
    try {
      const response = await axios.patch(`${API}/jobs/${project.id}`, {
        [field]: value
      });
      
      // Success - normal save flow
      setSaveStatus(SAVE_STATUS.SAVED);
      setLastSaved(new Date());
      setRetryField(null);
      setPendingDateSave(null);
      
      setTimeout(() => {
        setSaveStatus(prev => prev === SAVE_STATUS.SAVED ? SAVE_STATUS.IDLE : prev);
      }, 2000);
      
      if (onRefresh) {
        onRefresh();
      }
      
      toast.success('Schedule date updated');
      
    } catch (error) {
      console.error('Schedule save error:', error);
      
      // Check if it's a capacity conflict (409)
      if (error.response?.status === 409) {
        const conflictData = error.response.data?.detail?.capacity || error.response.data?.capacity;
        setCapacityConflict({
          market_name: conflictData?.market_name || 'This territory',
          week_start_date: conflictData?.week_start_date || value,
          next_available_weeks: conflictData?.next_available_weeks || [],
          can_override: error.response.data?.detail?.can_override || false
        });
        setCapacityConflictOpen(true);
        setSaveStatus(SAVE_STATUS.IDLE);
        return;
      }
      
      // Other error
      setSaveStatus(SAVE_STATUS.ERROR);
      setRetryField({ field, value });
      setPendingDateSave(null);
      toast.error(`Failed to save ${field.replace(/_/g, ' ')}`);
    }
  };
  
  // Handle selecting alternate week from capacity modal
  const handleSelectAlternateWeek = async (weekDate) => {
    setCapacityConflictOpen(false);
    
    try {
      await axios.patch(`${API}/jobs/${project.id}`, {
        estimated_start_date: weekDate
      });
      
      toast.success(`Rescheduled to week of ${new Date(weekDate + 'T00:00:00').toLocaleDateString()}`);
      
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('Reschedule error:', error);
      toast.error('Failed to reschedule');
    }
    
    setCapacityConflict(null);
    setPendingDateSave(null);
  };
  
  // Handle capacity override (PM/Ops only)
  const handleCapacityOverride = async (reason) => {
    setCapacityConflictOpen(false);
    
    if (!pendingDateSave) {
      toast.error('No pending date to save');
      return;
    }
    
    try {
      await axios.patch(`${API}/jobs/${project.id}`, {
        [pendingDateSave.field]: pendingDateSave.value,
        capacity_override_reason: reason
      });
      
      toast.success('Scheduled with override (audit logged)');
      
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('Override error:', error);
      toast.error(error.response?.data?.detail?.message || 'Failed to override');
    }
    
    setCapacityConflict(null);
    setPendingDateSave(null);
  };

  // Get user name helper
  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? `${foundUser.first_name} ${foundUser.last_name}` : '';
  };

  // Format date for display (unused but kept for future use)
  // const formatDate = (dateStr) => {
  //   if (!dateStr) return '—';
  //   try {
  //     return new Date(dateStr).toLocaleDateString('en-US', {
  //       year: 'numeric',
  //       month: 'short',
  //       day: 'numeric'
  //     });
  //   } catch {
  //     return dateStr;
  //   }
  // };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if HOA approval is required
  const hoaApprovalRequired = project?.hoa_info === 'required' || project?.hoa_info === 'pending';

  // Sales users for dropdowns
  const salesUsers = users.filter(u => 
    ['sales', 'sales_rep', 'sales_manager', 'director_of_sales'].includes(u.role)
  ).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }));

  // Ops users for dropdowns
  const opsUsers = users.filter(u => 
    ['ops', 'operations', 'ops_manager', 'foreman', 'project_manager'].includes(u.role)
  ).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }));

  // Market options
  const marketOptions = markets.map(m => ({ value: m.id, label: m.name }));

  if (!project) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-slate-500">No project selected</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Save Status Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hoaApprovalRequired && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              HOA Approval Required
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          {saveStatus === SAVE_STATUS.SAVING && (
            <span className="flex items-center gap-1.5 text-blue-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Saving...
            </span>
          )}
          {saveStatus === SAVE_STATUS.SAVED && (
            <span className="flex items-center gap-1.5 text-green-600">
              <Check className="h-3.5 w-3.5" />
              Saved
            </span>
          )}
          {saveStatus === SAVE_STATUS.ERROR && (
            <span className="flex items-center gap-1.5 text-red-600">
              <X className="h-3.5 w-3.5" />
              Error
              <button
                onClick={handleRetry}
                className="ml-1 underline hover:no-underline"
              >
                Retry
              </button>
            </span>
          )}
          {lastSaved && saveStatus === SAVE_STATUS.IDLE && (
            <span className="text-slate-400 text-xs">
              Last saved {formatDateTime(lastSaved)}
            </span>
          )}
        </div>
      </div>

      {/* Two-column responsive grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 1: Project Overview */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-4">
            <SectionHeader icon={Building} title="Project Overview" />
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <EditableField
              label="Client"
              value={project.customer_name}
              field="customer_name"
              required
              disabled={!permissions.general}
              onSave={handleSave}
              placeholder="Client name"
            />
            <EditableField
              label="Project Name"
              value={project.job_name}
              field="job_name"
              required
              disabled={!permissions.general}
              onSave={handleSave}
              placeholder="Project nickname"
            />
            <EditableField
              label="Project Type"
              value={project.job_type}
              field="job_type"
              type="select"
              options={PROJECT_TYPES}
              required
              disabled={!permissions.general}
              onSave={handleSave}
            />
            <EditableField
              label="Class (Market)"
              value={project.market_id}
              field="market_id"
              type="select"
              options={marketOptions}
              required
              disabled={!permissions.general}
              onSave={handleSave}
            />
            <EditableField
              label="Sales Person"
              value={project.assigned_to}
              field="assigned_to"
              type="select"
              options={salesUsers}
              disabled={!permissions.general}
              onSave={handleSave}
            />
            <EditableField
              label="Sales Person 2"
              value={project.sales_person_2}
              field="sales_person_2"
              type="select"
              options={salesUsers}
              disabled={!permissions.general}
              onSave={handleSave}
            />
            <EditableField
              label="Project Manager"
              value={project.project_manager}
              field="project_manager"
              type="select"
              options={opsUsers}
              disabled={!permissions.general}
              onSave={handleSave}
            />
            <EditableField
              label="Foreman"
              value={project.foreman}
              field="foreman"
              type="select"
              options={opsUsers}
              disabled={!permissions.general}
              onSave={handleSave}
            />
          </CardContent>
        </Card>

        {/* Section 2: Site & Scheduling */}
        <Card>
          <CardHeader className="pb-4">
            <SectionHeader icon={MapPin} title="Site & Scheduling" />
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField
              label="Street Address"
              value={project.address}
              field="address"
              required
              disabled={!permissions.scheduling}
              onSave={handleSave}
              placeholder="123 Main Street"
            />
            <div className="grid grid-cols-2 gap-4">
              <EditableField
                label="Apt / Suite"
                value={project.apt_suite}
                field="apt_suite"
                disabled={!permissions.scheduling}
                onSave={handleSave}
                placeholder="Apt 4B"
              />
              <EditableField
                label="City"
                value={project.city}
                field="city"
                disabled={!permissions.scheduling}
                onSave={handleSave}
                placeholder="Jacksonville"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <EditableField
                label="State"
                value={project.state}
                field="state"
                type="select"
                options={US_STATES}
                disabled={!permissions.scheduling}
                onSave={handleSave}
              />
              <EditableField
                label="Zip Code"
                value={project.zip_code}
                field="zip_code"
                disabled={!permissions.scheduling}
                onSave={handleSave}
                placeholder="32256"
              />
            </div>
            <Separator className="my-4" />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <EditableField
                    label="Estimated Start Date"
                    value={project.estimated_start_date?.split('T')[0]}
                    field="estimated_start_date"
                    type="date"
                    required
                    disabled={!permissions.scheduling}
                    onSave={handleScheduleDateSave}
                  />
                  {project.market_id && project.estimated_start_date && (
                    <div className="pt-6">
                      <CapacityIndicator
                        status={capacityCheck.status}
                        availableSlots={capacityCheck.availableSlots}
                        totalSlots={capacityCheck.totalSlots}
                        marketName={capacityCheck.marketName}
                        weekStartDate={capacityCheck.weekStartDate}
                      />
                    </div>
                  )}
                </div>
              </div>
              <EditableField
                label="Completed Date"
                value={project.completed_date?.split('T')[0]}
                field="completed_date"
                type="date"
                disabled={!permissions.scheduling}
                onSave={handleSave}
              />
            </div>
            <ReadOnlyField
              label="Duration"
              value={duration}
            />
          </CardContent>
        </Card>

        {/* Section 3: Financial Snapshot */}
        <Card>
          <CardHeader className="pb-4">
            <SectionHeader icon={DollarSign} title="Financial Snapshot">
              {!permissions.financials && (
                <Badge variant="secondary" className="text-xs">Read Only</Badge>
              )}
            </SectionHeader>
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField
              label="Estimated Project Value"
              value={project.estimated_value}
              field="estimated_value"
              type="number"
              required
              disabled={!permissions.financials}
              onSave={handleSave}
              placeholder="0.00"
            />
            <EditableField
              label="Payment Plan"
              value={project.payment_plan}
              field="payment_plan"
              type="select"
              options={PAYMENT_PLANS}
              required
              disabled={!permissions.financials}
              onSave={handleSave}
            />
            <Separator className="my-4" />
            <EditableField
              label="Allow Credit Card Payments"
              value={project.allow_credit_card}
              field="allow_credit_card"
              type="switch"
              disabled={!permissions.financials}
              onSave={handleSave}
            />
            <EditableField
              label="Allow ACH Payments"
              value={project.allow_ach}
              field="allow_ach"
              type="switch"
              disabled={!permissions.financials}
              onSave={handleSave}
            />
          </CardContent>
        </Card>

        {/* Section 4: Scope & Specifications */}
        <Card>
          <CardHeader className="pb-4">
            <SectionHeader icon={Wrench} title="Scope & Specifications" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <EditableField
                label="Warranty"
                value={project.warranty}
                field="warranty"
                type="select"
                options={WARRANTIES}
                required
                disabled={!permissions.general}
                onSave={handleSave}
              />
              <EditableField
                label="Square Footage"
                value={project.square_footage}
                field="square_footage"
                type="number"
                required
                disabled={!permissions.general}
                onSave={handleSave}
                placeholder="0"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <EditableField
                label="Product Used"
                value={project.product_used}
                field="product_used"
                type="select"
                options={PRODUCTS}
                required
                disabled={!permissions.general}
                onSave={handleSave}
              />
              <EditableField
                label="Color Change?"
                value={project.color_change}
                field="color_change"
                type="switch"
                required
                disabled={!permissions.general}
                onSave={handleSave}
              />
            </div>
            <EditableField
              label="Colors"
              value={project.colors}
              field="colors"
              multiline
              required
              disabled={!permissions.general}
              onSave={handleSave}
              placeholder="Enter color specifications..."
            />
            <EditableField
              label="Repairs Committed with Sale"
              value={project.repairs_committed}
              field="repairs_committed"
              multiline
              required
              disabled={!permissions.general}
              onSave={handleSave}
              placeholder="Describe any repairs committed..."
            />
          </CardContent>
        </Card>

        {/* Section 5: HOA & Compliance */}
        <Card>
          <CardHeader className="pb-4">
            <SectionHeader icon={Shield} title="HOA & Compliance" />
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField
              label="HOA Approval Status"
              value={project.hoa_info}
              field="hoa_info"
              type="select"
              options={HOA_APPROVAL_STATUS}
              required
              disabled={!permissions.general}
              onSave={handleSave}
            />
            {(project.hoa_info && project.hoa_info !== 'not_required') && (
              <EditableField
                label="HOA Notes"
                value={project.hoa_notes}
                field="hoa_notes"
                multiline
                disabled={!permissions.general}
                onSave={handleSave}
                placeholder="HOA contact info, requirements, deadlines..."
              />
            )}
            {hoaApprovalRequired && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>This project requires HOA approval before proceeding to Production.</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 6: Production Notes */}
        <Card>
          <CardHeader className="pb-4">
            <SectionHeader icon={FileText} title="Production Notes">
              {!permissions.productionNotes && (
                <Badge variant="secondary" className="text-xs">Read Only</Badge>
              )}
            </SectionHeader>
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField
              label="Production Notes"
              value={project.production_notes}
              field="production_notes"
              multiline
              disabled={!permissions.productionNotes}
              onSave={handleSave}
              placeholder="Internal notes for production team..."
              className="min-h-[150px]"
            />
            {project.updated_at && (
              <div className="text-xs text-slate-400">
                Last updated: {formatDateTime(project.updated_at)}
                {project.updated_by && ` by ${getUserName(project.updated_by)}`}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 7: Post-Completion / Feedback */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-4">
            <SectionHeader icon={Star} title="Post-Completion & Feedback" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <EditableField
                label="5 Star Review Received"
                value={project.five_star_review}
                field="five_star_review"
                type="switch"
                disabled={!permissions.general}
                onSave={handleSave}
              />
              <ReadOnlyField
                label="Created Date"
                value={formatDateTime(project.created_at)}
              />
              <ReadOnlyField
                label="Created By"
                value={getUserName(project.created_by)}
              />
              <ReadOnlyField
                label="Updated Date"
                value={formatDateTime(project.updated_at)}
              />
              <ReadOnlyField
                label="Updated By"
                value={getUserName(project.updated_by)}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Capacity Full Modal */}
      <CapacityFullModal
        open={capacityConflictOpen}
        onClose={() => {
          setCapacityConflictOpen(false);
          setPendingDateSave(null);
          setCapacityConflict(null);
        }}
        marketName={capacityConflict?.market_name}
        weekStartDate={capacityConflict?.week_start_date}
        nextAvailableWeeks={capacityConflict?.next_available_weeks || []}
        userCanOverride={canOverrideCapacity}
        onSelectWeek={handleSelectAlternateWeek}
        onOverride={handleCapacityOverride}
        onPickAnother={() => {
          setCapacityConflictOpen(false);
          setPendingDateSave(null);
        }}
      />
    </div>
  );
};

export default ProjectDetailsTab;
